import { AfterViewChecked, AfterViewInit, Component, ContentChildren, ElementRef, EventEmitter, forwardRef, Injector, Input, OnDestroy, OnInit, Output, Provider, QueryList, Type, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BaseComponent } from "ng/src/app/shared/base.component";
import { PagedArray } from "app/shared/model/app.model";
import { GeneralParameters } from "app/shared/model/request-parameter.model";
import { Utils } from "ng/src/app/core/utils";
import { HALPageDetails } from "ng/src/app/shared/model/app.model";
import { PrimeTemplate } from "primeng/api";
import { AutoComplete } from "primeng/autocomplete";
import { BehaviorSubject, fromEvent, Observable, of, race, Subject } from "rxjs";
import { delay, filter, map, skip, skipWhile, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { QuerySelectorType } from './query-selector.model';

/**
 * When implementing this component the containing module must import `AutoCompleteModule`
 * and the template must point to `query-selector.component.html` and styles to `query-selector.component.scss`.
 * Providers in the component annotation should include for the `ControlValueAccessor` or use `QuerySelectorComponent.ProvideAccessor()`.
 */
@Component({template: ''})
export abstract class QuerySelectorComponent<
	T extends any,
	P extends GeneralParameters
> extends BaseComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy, ControlValueAccessor {
	@ViewChild('autoComplete') autoComplete!: AutoComplete;
	@ContentChildren(PrimeTemplate) templates!: QueryList<any>;
	
	@Input() maxItems: null | number = null;
	@Input() inputId: string = '';
	@Input() placeholder = '';
	@Input() emptyListMessage = '';
	
	@Input()
	abstract type: QuerySelectorType;
	
	/**
	 * Whether the model and form updates should be null or the single value instead of an array depending on
	 * the size of the selection
	 */
	@Input() singleValue = false;
	
	@Output() onInit = new EventEmitter();
	@Output() afterViewInit = new EventEmitter();
	@Output() onDestroy = new EventEmitter();
	
	onChange: (val: any) => void = () => {}
	onTouched = () => {}
	
	filteredSuggestions: T[] = [];
	suggestions: T[] = [];
	
	// The query keys are used to determine if a query has already been executed.
	queryKeys: Array<Array<unknown>> = [];
	disabled = false;
	
	protected _value: T[] = [];
	protected pageSize = 25;
	
	protected pageDetails?: HALPageDetails;
	protected currentSearch = "";
	
	private readonly queryExecutingSubject = new BehaviorSubject<boolean>(false);
	readonly queryExecuting$ = this.queryExecutingSubject.asObservable().pipe(skip(1));
	readonly elementRef!: ElementRef;
	
	protected constructor(
		injector: Injector
	) {
		super(injector);
		this.onDestroy$.pipe(
			take(1)
		)
			.subscribe(() => this.onDestroy.emit());
		
		this.elementRef = injector.get(ElementRef);
	}
	
	get queryExecuting() {
		return this.queryExecutingSubject.value;
	}
	
	get page() {
		return this.pageDetails?.number ?? 0;
	}
	
	get totalPages() {
		return this.pageDetails?.totalPages ?? -1;
	}
	
	get hasMore() {
		return this.page < this.totalPages;
	}
	
	get value() {
		return this._value
	}
	
	set value(val) {
		this.writeValue(val)
	}
	
	get useVirtualScroll() {
		return this.totalPages > 1;
	}
	
	get isDisabled(): boolean {
		return this.disabled;
	}
	
	get isInvalid() {
		return (this.elementRef.nativeElement as HTMLElement).classList.contains('ng-invalid');
	}
	
	ngOnInit(): void {
		this.onInit.emit();
	}
	
	ngAfterViewInit(): void {
		this.templates.forEach((item) => {
			if (!(item instanceof PrimeTemplate)) return;
			
			const template = item.template;
			
			switch (item.getType()) {
				case 'header':
					this.autoComplete.headerTemplate = template;
					break;
				case 'footer':
					this.autoComplete.footerTemplate = template;
					break;
				case 'empty':
					this.autoComplete.emptyTemplate = template;
					break;
				case 'selectedItem':
					this.autoComplete.selectedItemTemplate = template;
					break;
				case 'item':
					this.autoComplete.itemTemplate = template;
					break;
			}
		})
		
		const dropdownBtn = (this.autoComplete.dropdownButton?.nativeElement as HTMLElement);
		if (dropdownBtn) {
			dropdownBtn.tabIndex = -1;
		}
		
		const input = (this.autoComplete.multiInputEL?.nativeElement as HTMLInputElement);
		
		const container = (this.autoComplete.multiContainerEL.nativeElement as HTMLElement);
		
		const originalHide = this.autoComplete.hide.bind(this.autoComplete);
		
		// The last click event from the Container is used to determine if a hide event originated from the input.
		let clickEv: MouseEvent | null = null;
		
		// Override the hide method to prevent it from closing when the input is focused due to bubbled events from its
		// parent container.
		this.autoComplete.hide = () => {
			const acFilled = this.autoComplete.filled as unknown as number | boolean;
			
			let filled: boolean;
			if (typeof acFilled === 'number') {
				if (typeof this.maxItems === 'number' && !isNaN(this.maxItems) && this.maxItems > 0) {
					filled = this.maxItems >= acFilled;
				}
				else {
					filled = this.type === QuerySelectorType.Single && acFilled > 0;
				}
			}
			else {
				filled = acFilled;
			}
			
			if (this.filteredSuggestions.length === 0) {
				filled = true;
			}
			
			if (this.queryExecuting !== (clickEv != null) || (!filled && input === document.activeElement)) {
				return;
			}
			
			originalHide();
		}
		
		if (container) {
			const originalOnClick = container.onclick;
			
			container.onclick = (ev) => {
				clickEv = ev;
				const target = ev.currentTarget as HTMLInputElement | null;
				if (target != null && document.activeElement === input) {
					
					if (!this.autoComplete.overlayVisible) {
						this.autoComplete.search(ev, input.value);
					}
				}
				
				else {
					originalOnClick?.call(container, ev);
				}
				
				setTimeout(() => {
					clickEv = null;
				})
			};
		}
		
		if (input) {
			// Adding our own input event for when the input is empty because AutoComplete does not trigger automatically
			fromEvent(input, 'input')
				.pipe(
					takeUntil(this.onDestroy$),
					switchMap((ev) => {
						const val = input.value;
						return of(ev).pipe(
							filter(() => input.value.length === 0),
							delay(300),
							filter(() => input.value === val)
						)
					})
				)
				.subscribe((ev) => {
					this.onComplete({ originalEvent: ev, query: this.currentSearch });
				});
		}
		
		this.queryExecuting$.subscribe((val) => {
			this.autoComplete.loading = val;
			this.autoComplete.cd.detectChanges();
		});
		
		// Replace the original handleSuggestionsChange method as it will complete itself early in some
		// states and does not show the loading indicator
		const original = this.autoComplete.handleSuggestionsChange.bind(this.autoComplete);
		this.autoComplete.handleSuggestionsChange = () => {
			if (!this.queryExecuting)
				original();
		};
		
		
		this.afterViewInit.emit();
	}
	
	ngAfterViewChecked(): void {
		
		// Workaround for z-index issue when we are inside a dialog or overlay
		const overlayEl = this.autoComplete.overlay;
		if (this.autoComplete.overlayVisible && overlayEl) {
			const rect = overlayEl.getBoundingClientRect();
			const centerX = rect.left + (rect.width / 2);
			const centerY = rect.top + (rect.height / 2);
		  const topElement = document.elementFromPoint(centerX, centerY);
			
			if (topElement && topElement !== overlayEl) {
				let parent = topElement.parentElement;
				while (parent != null) {
					const zIndex = parseInt(getComputedStyle(parent).zIndex);
					if (!isNaN(zIndex)) {
						overlayEl.style.zIndex = `${zIndex + 1}`;
						break;
					}
					
					parent = parent.parentElement;
				}
			}
			
		}
	}
	
	registerOnChange(fn: any): void {
		this.onChange = fn;
	}
	
	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}
	
	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}
	
	writeValue(val: T[] | T | null): void {
		let newVal = (Array.isArray(val) ? val : val != null ? [val] : []);
		
		/**
		 * Verify that the new value and the auto complete value match to determine that it is a legitimate
		 * change to send to the onChange event. Otherwise, the event could be coming from outside the component and
		 * be invalid causing random ngModel updates.
		 */
		const changeFromAutoComplete = this.autoComplete && this.autoComplete.value === val;
		const emit = changeFromAutoComplete && this.hasChangesWithValue(newVal);
		
		if ((this.type === QuerySelectorType.Single || this.maxItems === 1) && newVal.length > 1) {
			newVal = newVal.filter(nVal => !this.value.some(oVal => this.getItemId(nVal) === this.getItemId(oVal)));
		}
		
		this._value = newVal;
		if (this.pageDetails) {
			const update = PagedArray.from(this.suggestions, this.pageDetails);
			this.updateSuggestions(update);
		}
		
		if (emit) {
			if (this.singleValue && this.value.length <= 1) {
				this.onChange(this.value[0] ?? null);
			}
			else {
				this.onChange(this.value);
			}
		}
	}
	
	private hasChangesWithValue(newVal: T[] | null) {
		if (newVal == null) return false;
		const newMap = Utils.arrayToMap(newVal, i => this.getItemId(i));
		const valueMap = Utils.arrayToMap(this.value, i => this.getItemId(i));
		
		if (Object.keys(newMap).length !== Object.keys(valueMap).length) return true;
		return !Object.keys(newMap).every(nKey => nKey in valueMap);
	}
	
	private getItemId(val: T): any {
		return val[this.idField];
	}
	
	private updateFrom(ob$: Observable<PagedArray<T>>) {
		this.queryExecutingSubject.next(true);
		ob$.pipe(
			takeUntil(
				race([
					this.onDestroy$,
					this.queryExecuting$.pipe(
						skipWhile(val => !val)
					)])
			),
			tap(() => this.queryExecutingSubject.next(false))
		)
			.subscribe(val => this.updateSuggestions(val));
	}
	
	updateSuggestions(val: PagedArray<T>) {
		//Sets loading to true as autocomplete will not update its suggestions if it thinks it isn't loading
		this.autoComplete.loading = true;
		this.suggestions = [...val];
		this.pageDetails = val.page;
		this.filteredSuggestions = this.filterSelectedFromSuggestions(val);
	}
	
	onComplete(input: { originalEvent: Event, query: string }) {
		// Get the input value from the auto complete element instead of the event because the event from a dropdown
		// will not contain the input value.
		this.currentSearch = (this.autoComplete.multiInputEL.nativeElement as HTMLInputElement | undefined)?.value ?? input.query;
		const params: P = {
			...this.constructQuery(this.currentSearch),
			size: this.pageSize,
			page: this.page
		};
		
		delete this.pageDetails;
		this.filteredSuggestions = [];
		this.queryKeys = [];
		
		this.updateFrom(this.fetch(params));
	}
	
	onPage() {
		if (!this.hasMore) return;
		
		const params: P = {
			...this.constructQuery(this.currentSearch),
			size: this.pageSize,
			page: this.page + 1
		}
		
		const queryKey = [params.page];
		if (this.hasQueryKey(queryKey)) {
			return;
		}
		
		this.queryKeys.push(queryKey);
		
		const ob$ = this.fetch(params).pipe(
			map((val) => {
				return PagedArray.from([
					...this.suggestions,
					...val
				], val.page)
			})
		);
		
		this.updateFrom(ob$);
	}
	
	showingSuggestions() {
		setTimeout(() => {
			const scrollCtr = this.autoComplete.itemsWrapper;
			if (!scrollCtr) return;
			
			scrollCtr.addEventListener('scroll', (event: Event) => {
				const scrollHeight = scrollCtr.scrollHeight - scrollCtr.clientHeight;
				const shouldTrigger = scrollCtr.scrollTop >= scrollHeight * 0.90;
				
				if (shouldTrigger) this.onPage();
			});
		});
	}
	
	filterSelectedFromSuggestions(newSuggestions: T[] = this.filteredSuggestions): T[] {
		const map = Utils.arrayToMap(this.value, i => this.getItemId(i));
		return newSuggestions.filter(item => !(this.getItemId(item) in map))
	}
	
	hidingSuggestions() {
		delete this.pageDetails;
	}
	
	private hasQueryKey(queryKey: Array<unknown>): boolean {
		return this.queryKeys.some((key) => {
			return key.length === queryKey.length && key.every((val, index) => val === queryKey[index]);
		});
	}
	
	
	abstract displayField: keyof T;
	abstract idField: keyof T;
	
	protected abstract constructQuery(query: string): P;
	protected abstract fetch(query: P): Observable<PagedArray<T>>;
	
	public static ProviderAccessor(type: Type<ControlValueAccessor>): Provider {
		return {
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => type)
		}
	}
}